import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import logoOnLight from '../assets/decisionpoint-logo-light-bg.png';

export interface AuthLayoutProps {
  /** Card body — the screen-specific content (title, message, action). */
  children: ReactNode;
  /** Optional muted footer rendered below the card (support line, attribution). */
  footer?: ReactNode;
  /** Slot rendered ABOVE the card (e.g. the signed-out banner). */
  bannerAbove?: ReactNode;
  /** Indeterminate top progress bar inside the card (the signing-in screen). */
  topProgress?: boolean;
  /** Card max width in px. */
  maxWidth?: number;
}

/** Polaris elevation-2 (colors_and_type.css `--elev-2`). */
const ELEV_2 = '0 2px 6px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.05)';

/**
 * Centered neutral-brand card on a plain background — the shared frame for every
 * pre-auth / access-refused screen (sign-in landing, "signing you in", blocked
 * dead-ends, signed-out). Renders OUTSIDE the `AppShell`: no sidebar, no TopBar,
 * no customer branding. The DecisionPoint logo (neutral pre-auth identity) sits
 * centered at the top; each screen centers its own body. Everything is
 * token-tied so it stays on the neutral brand until `whoami` resolves.
 */
export function AuthLayout({
  children,
  footer,
  bannerAbove,
  topProgress = false,
  maxWidth = 400,
}: AuthLayoutProps) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth }}>
        {bannerAbove}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            boxShadow: ELEV_2,
            p: '40px 36px 28px',
          }}
        >
          {topProgress && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                bgcolor: 'rgba(0, 71, 133, 0.10)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  height: '100%',
                  width: '35%',
                  bgcolor: 'primary.main',
                  borderRadius: '2px',
                  animation: 'auth-progress 1.25s cubic-bezier(0.4,0,0.2,1) infinite',
                  '@keyframes auth-progress': {
                    '0%': { left: '-35%' },
                    '100%': { left: '100%' },
                  },
                }}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: '28px' }}>
            <Box
              component="img"
              src={logoOnLight}
              alt="DecisionPoint"
              sx={{ height: 26, width: 'auto' }}
            />
          </Box>
          {children}
        </Box>
        {footer && (
          <Box sx={{ mt: '22px', textAlign: 'center', color: 'text.secondary' }}>{footer}</Box>
        )}
      </Box>
    </Box>
  );
}
